import { ArrowDropDown, KeyboardArrowDown } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Collapse,
  Drawer,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  selectConditions,
  selectGenres,
  selectScenarios,
} from "features/homepage/saga/homepageSlice";
import LabelCheckbox from "features/searchStore/components/LabelCheckbox";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { searchStoreActions, selectFilter } from "../saga/searchStoreSlice";
import { ISearchFilter } from "../models";
import { generateBudgetOptions } from "helpers/generateBudgetOptions";
import { useNavigate } from "react-router-dom";

export function CategoryDrawer({ handleClose }: { handleClose: () => void }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const filter = useAppSelector(selectFilter);

  const genreList = useAppSelector(selectGenres);
  const conditionList = useAppSelector(selectConditions);
  const scenarioList = useAppSelector(selectScenarios);

  const [selectedGenres, setSelectedGenres] = useState<string[]>(filter.genres);
  const [genresStr, setGenresStr] = useState<string[]>([]);

  const [selectedConditions, setSelectedConditions] = useState<string[]>(
    filter.conditions
  );
  const [selectedScenes, setSelectedScenes] = useState<string[]>(filter.scenes);

  const [openGenre, setOpenGenre] = useState(false);
  const [openScene, setOpenScene] = useState(false);
  const [openCondition, setOpenCondition] = useState(false);

  const handleSelectGenre = (value: any, label: any) => {
    if (!selectedGenres.includes(value)) {
      setSelectedGenres((pre) => [...pre, value]);
      setGenresStr((pre) => [...pre, label]);
    } else {
      setSelectedGenres(selectedGenres.filter((id) => id !== value));
      setGenresStr(genresStr.filter((id) => id !== label));
    }
  };

  const handleSelectCondition = (e: any) => {
    if (!selectedConditions.includes(e)) {
      setSelectedConditions((pre) => [...pre, e]);
    } else {
      const remain = selectedConditions.filter((id) => id !== e);
      setSelectedConditions(remain);
    }
  };

  const handleSelectScene = (e: any) => {
    if (!selectedScenes.includes(e)) {
      setSelectedScenes((pre) => [...pre, e]);
    } else {
      const remain = selectedScenes.filter((id) => id !== e);
      setSelectedScenes(remain);
    }
  };

  const handleOpenCondition = () => setOpenCondition(!openCondition);

  const handleOpenScene = () => setOpenScene(!openScene);

  const handleSearch = () => {
    const newFilter: ISearchFilter = {
      ...filter,
      genres: selectedGenres,
      scenes: selectedScenes,
      conditions: selectedConditions,
    };
    dispatch(searchStoreActions.setSearchFilter(newFilter));
    handleClose();
    navigate("/stores");
  };

  return (
    <Box
      sx={{
        overflowY: "auto",
        py: "16px",
      }}
    >
      <Box my={1} px="16px" onClick={() => setOpenGenre(true)}>
        <Typography fontWeight="bold" sx={{ my: 2 }}>
          料理ジャンルから探す
        </Typography>
        <Box border={1} p={1} display="flex" borderColor="divider">
          <Box
            display="flex"
            flex={1}
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {genresStr.length === 0 ? (
              <Typography>指定なし</Typography>
            ) : (
              <Typography noWrap>{genresStr.join(", ")}</Typography>
            )}
          </Box>
          {selectedGenres.length === 0 ? (
            <ArrowDropDown />
          ) : (
            <Avatar
              sx={{
                bgcolor: "primary.main",
                fontSize: "small",
                height: "20px",
                width: "20px",
              }}
            >
              {selectedGenres.length}
            </Avatar>
          )}
        </Box>
      </Box>

      <Drawer
        anchor="bottom"
        open={openGenre}
        onClose={() => setOpenGenre(false)}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          height: "100%",
          margin: 0,
          padding: 0,
        }}
        PaperProps={{
          style: {
            position: "absolute",
            height: "90%",
            margin: 0,
            padding: 0,
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          width="100%"
          onClick={() => setOpenGenre(false)}
        >
          <KeyboardArrowDown fontSize="large" />
        </Box>
        <Grid
          container
          sx={{
            overflowY: "auto",
            py: "16px",
            // marginBottom: "80px",
          }}
        >
          {genreList.map((e, index) => (
            <LabelCheckbox
              xs={6}
              value={e.Id}
              boxLabel={e.Name}
              p={1}
              borderBottom={1}
              borderRight={1}
              borderColor="divider"
              sx={{
                marginBottom: '-1px',
                marginRight: '-1px',
              }}
              onSelected={handleSelectGenre}
              defaultState={selectedGenres.includes(e.Id)}
            />
          ))}
        </Grid>
      </Drawer>

      <Box my={2} px="16px">
        <Typography fontWeight="bold" sx={{ my: 2 }}>
          予算
        </Typography>
        <Box display="flex" alignItems="center">
          <FormControl fullWidth size="small">
            <Select
              placeholder="Min"
              defaultValue="min"
              fullWidth
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
            >
              <MenuItem value="min">下限なし</MenuItem>
              {generateBudgetOptions()}
            </Select>
          </FormControl>

          <Typography mx={2}>~</Typography>

          <FormControl fullWidth size="small">
            <Select
              placeholder="Max"
              defaultValue="max"
              fullWidth
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
            >
              <MenuItem value="max">上限なし</MenuItem>
              {generateBudgetOptions()}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box marginTop={2}>
        <Box
          display="flex"
          alignItems="center"
          px="16px"
          py={2}
          border={1}
          borderColor="divider"
          onClick={handleOpenCondition}
        >
          <Typography fontWeight="bold" sx={{ display: "flex", flex: 1 }}>
            こだわり条件から探す
          </Typography>
          <ArrowDropDown />
        </Box>
        <Collapse in={openCondition} timeout="auto" unmountOnExit>
          <Grid container borderBottom={1} borderColor="divider">
            {conditionList.map((e, index) => (
              <LabelCheckbox
                key={e.Id}
                xs={6}
                value={e.Id}
                boxLabel={e.Name}
                p={1}
                borderBottom={index !== conditionList.length - 1 ? 1 : 0}
                borderRight={1}
                borderColor="divider"
                sx={{
                  marginBottom: index === conditionList.length - 1 ? "-1px" : 0,
                  marginRight: index === conditionList.length - 1 ? "-1px" : 0,
                }}
                onSelected={handleSelectCondition}
                defaultState={selectedConditions.includes(e.Id)}
              />
            ))}
          </Grid>
        </Collapse>
      </Box>

      <Box marginBottom="80px">
        <Box
          display="flex"
          alignItems="center"
          px="16px"
          py={2}
          borderBottom={1}
          borderColor="divider"
          onClick={handleOpenScene}
        >
          <Typography fontWeight="bold" sx={{ display: "flex", flex: 1 }}>
            利用シーンから探す
          </Typography>
          <ArrowDropDown />
        </Box>
        <Collapse in={openScene} timeout="auto" unmountOnExit>
          <Grid container borderBottom={1} borderColor="divider">
            {scenarioList.map((e, index) => (
              <LabelCheckbox
                key={e.Id}
                xs={6}
                value={e.Id}
                boxLabel={e.Name}
                p={1}
                borderBottom={index !== scenarioList.length - 1 ? 1 : 0}
                borderRight={1}
                borderColor="divider"
                sx={{
                  marginBottom: index === scenarioList.length - 1 ? "-1px" : 0,
                  marginRight: index === scenarioList.length - 1 ? "-1px" : 0,
                }}
                defaultState={selectedScenes.includes(e.Id)}
                onSelected={handleSelectScene}
              />
            ))}
          </Grid>
        </Collapse>
      </Box>

      <Box
        position="absolute"
        bottom={2}
        right={0}
        left={0}
        display="flex"
        justifyContent="center"
        p={2}
      >
        <Button variant="contained" onClick={handleSearch}>
          決定
        </Button>
      </Box>
    </Box>
  );
}
